import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { KNOWN_CATEGORIES, PRIORITY_OPTIONS, statusLabel } from './pendencies-adapter';

import type { DueWindow } from './pendencies-adapter';
import type { PendencyWireItem, PendencyWireStatus } from '../../lib/api-client';
import type { JSX } from 'react';

export type StatusFilter = PendencyWireStatus | 'all';

export interface PendencyFilters {
  /** Server-side (D2) — único `status` aceito pelo GET; 'all' = sem filtro. */
  status: StatusFilter;
  /** Client-side (D2). */
  priorities: PendencyWireItem['priority'][];
  categories: string[];
  dueWindow: DueWindow | null;
}

export const EMPTY_FILTERS: PendencyFilters = {
  status: 'all',
  priorities: [],
  categories: [],
  dueWindow: null,
};

const STATUS_OPTIONS: StatusFilter[] = [
  'all',
  'open',
  'in_progress',
  'postponed',
  'resolved',
  'cancelled',
  'archived',
];

const DUE_OPTIONS: { key: DueWindow; label: string }[] = [
  { key: 'overdue', label: 'Vencida' },
  { key: 'today', label: 'Hoje' },
  { key: 'next7', label: '7 dias' },
  { key: 'none', label: 'Sem prazo' },
];

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function Group({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="filter-group" data-collapsed={!open}>
      <button type="button" className="filter-group__head" onClick={() => onToggle(id)}>
        <span className="filter-group__title">{title}</span>
        <ChevronDown className="filter-group__chev" aria-hidden />
      </button>
      <div className="filter-group__body">{children}</div>
    </div>
  );
}

/**
 * Filter rail da Tela 3 (controlado). Estados = server-side (único `status`,
 * D2); prioridade/categoria/prazo = client-side. Reusa `.filter-*` da Inbox.
 */
export function PendencyFilterRail({
  filters,
  onChange,
}: {
  filters: PendencyFilters;
  onChange: (next: PendencyFilters) => void;
}): JSX.Element {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    states: true,
    priorities: true,
    categories: true,
    dueAt: false,
  });
  const tog = (id: string): void => setOpenGroups((g) => ({ ...g, [id]: !(g[id] ?? false) }));

  return (
    <aside className="filter-rail">
      <Group id="states" title="Estado" open={openGroups.states ?? false} onToggle={tog}>
        {STATUS_OPTIONS.map((s) => (
          <label key={s} className="filter-row">
            <input
              type="radio"
              name="pend-status"
              checked={filters.status === s}
              onChange={() => onChange({ ...filters, status: s })}
            />
            <span>{s === 'all' ? 'Todas' : statusLabel(s)}</span>
          </label>
        ))}
      </Group>

      <Group
        id="priorities"
        title="Prioridade"
        open={openGroups.priorities ?? false}
        onToggle={tog}
      >
        {PRIORITY_OPTIONS.map((p) => (
          <label key={p.key} className="filter-row">
            <input
              type="checkbox"
              checked={filters.priorities.includes(p.key)}
              onChange={() =>
                onChange({ ...filters, priorities: toggle(filters.priorities, p.key) })
              }
            />
            <span>{p.label}</span>
          </label>
        ))}
      </Group>

      <Group id="categories" title="Categoria" open={openGroups.categories ?? false} onToggle={tog}>
        {KNOWN_CATEGORIES.map((c) => (
          <label key={c.key} className="filter-row">
            <input
              type="checkbox"
              checked={filters.categories.includes(c.key)}
              onChange={() =>
                onChange({ ...filters, categories: toggle(filters.categories, c.key) })
              }
            />
            <span>{c.label}</span>
          </label>
        ))}
      </Group>

      <Group id="dueAt" title="Prazo" open={openGroups.dueAt ?? false} onToggle={tog}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {DUE_OPTIONS.map((d) => {
            const active = filters.dueWindow === d.key;
            return (
              <button
                key={d.key}
                type="button"
                className={`filter-chip ${active ? 'filter-chip--active' : ''}`.trim()}
                onClick={() => onChange({ ...filters, dueWindow: active ? null : d.key })}
              >
                {d.label}
              </button>
            );
          })}
        </div>
      </Group>
    </aside>
  );
}
